import {useMemo, useCallback, useEffect} from 'react'
import {
  ModelPickerDesign,
  type AutoTierPickerConfig,
} from '@github-ui/copilot-shared-ui/components/chat-model-picker/ModelPickerDesign'
import {useAgentModels} from '@github-ui/copilot-shared-ui/agents/use-agent-models'
import type {CopilotChatModel} from '@github-ui/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import type {IconButtonProps} from '@primer/react'

import {usePickerTelemetry} from '../telemetry/use-picker-telemetry'
import {useComposerTelemetry} from '../telemetry/ComposerTelemetryContext'
import {AUTO_MODEL_ID as AVAILABLE_AUTO_MODEL_ID, getAvailableAgentModels} from './available-agent-models'

export const AUTO_MODEL_ID = AVAILABLE_AUTO_MODEL_ID

const MODEL_PICKER_TELEMETRY = {
  opened: 'model_picker_opened',
  changed: 'model_changed',
  dismissed: 'model_picker_dismissed',
} as const

export interface ModelPickerProps {
  onUpdateModel: (modelId: string, options?: {reconcile?: boolean}) => void
  selectedModelId?: string
  /** Model identified by EMS as the organization's default for new sessions. */
  organizationDefaultModelId?: string
  /** When provided, only models whose vendor matches any of the filters (case-insensitive) are shown. */
  vendorFilters?: string[]
  /** Task payload-backed billing mode indicator. */
  isTokenBasedBillingEnabled?: boolean
  /** Concrete model selected by the Auto router for the active conversation. */
  autoPickedModel?: string
  /** When false, the trigger hides its leading vendor/Copilot icon and shows the model name only. */
  showTriggerIcon?: boolean
  /** Optional trigger size; the env-strip toolbar passes 'medium' because Primer's
   *  ActionMenu.Button clips text at 'small' (28px). Other callers may use 'small'. */
  size?: 'small' | 'medium' | 'large'
  /** Keeps the strip trigger mounted with inert skeleton content while selection resolves. */
  showLoadingState?: boolean
  disabled?: boolean
  /** Opts the trigger into the composer's container-query compaction. */
  responsiveIconOnly?: boolean
  tooltipDirection?: IconButtonProps['tooltipDirection']
  /**
   * Controlled Auto-tier presentation. Omit it for unsupported Hosts, flag-off
   * states, coding-agent flows, or any other surface that must hide tier selection.
   */
  autoTierPicker?: AutoTierPickerConfig
}

export function ModelPicker({
  onUpdateModel,
  selectedModelId,
  organizationDefaultModelId,
  vendorFilters,
  isTokenBasedBillingEnabled,
  autoPickedModel,
  showTriggerIcon = true,
  size,
  showLoadingState = false,
  disabled = false,
  responsiveIconOnly,
  tooltipDirection,
  autoTierPicker,
}: ModelPickerProps) {
  const {models: allModels, isLoading} = useAgentModels()

  const availableModels = useMemo(() => getAvailableAgentModels(allModels, vendorFilters), [allModels, vendorFilters])

  const foundModel = availableModels?.find(model => model.id === selectedModelId)
  const defaultModel = availableModels?.find(m => m.is_chat_default) ?? availableModels?.[0]
  const displayedModelId = (selectedModelId ? (foundModel ?? defaultModel) : defaultModel)?.id

  const telemetry = useComposerTelemetry()
  useEffect(() => {
    telemetry?.noteModel(displayedModelId)
  }, [displayedModelId, telemetry])
  const {onPicked: trackModelPicked, onOpenChange: trackModelPickerOpen} = usePickerTelemetry(MODEL_PICKER_TELEMETRY)
  const handleUpdateModel = useCallback(
    (model: CopilotChatModel) => {
      trackModelPicked(displayedModelId, model.id, {from_model_id: displayedModelId, to_model_id: model.id})
      onUpdateModel(model.id)
    },
    [displayedModelId, onUpdateModel, trackModelPicked],
  )
  const handleOpenChange = useCallback(
    (open: boolean) => trackModelPickerOpen(open, {from_model_id: displayedModelId}),
    [displayedModelId, trackModelPickerOpen],
  )

  // When the selected model doesn't exist in the (vendor-filtered) list,
  // auto-select the default model so the picker doesn't show "(unavailable)".
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- loading keeps this component mounted for focus continuity, but unresolved vendor scope must not commit a fallback
    if (!showLoadingState && selectedModelId && availableModels && availableModels.length > 0 && !foundModel) {
      const fallback = availableModels.find(m => m.is_chat_default) ?? availableModels[0]
      if (fallback) {
        onUpdateModel(fallback.id, {reconcile: true})
      }
    }
  }, [showLoadingState, selectedModelId, availableModels, foundModel, onUpdateModel])

  // If no model selected yet, use default
  const selectedModel = selectedModelId ? (foundModel ?? defaultModel) : defaultModel

  // When no explicit selection exists and models have loaded, persist the resolved
  // default so that other submit paths (e.g. slash command, task entrypoint) that
  // read from localStorage will send the correct model instead of omitting it.
  const defaultModelId = defaultModel?.id
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- loading keeps this component mounted for focus continuity, but unresolved vendor scope must not commit a default
    if (!showLoadingState && !selectedModelId && defaultModelId && !isLoading) {
      onUpdateModel(defaultModelId, {reconcile: true})
    }
  }, [showLoadingState, selectedModelId, defaultModelId, isLoading, onUpdateModel])

  const isOnlyAutoModel =
    copilotFeatureFlags.codingAgentHideModelPickerIfOnlyAuto &&
    (!vendorFilters || vendorFilters.length === 0) &&
    availableModels?.length === 1 &&
    availableModels[0]?.id === AUTO_MODEL_ID

  if (showLoadingState) {
    return (
      <ModelPickerDesign
        disabled={disabled}
        type="global"
        variant="invisible"
        isLoading
        showTriggerIcon={false}
        size={size}
      />
    )
  }

  if (isLoading || !availableModels || !selectedModel || isOnlyAutoModel) return null

  return (
    <ModelPickerDesign
      models={availableModels}
      selectedModel={selectedModel}
      onModelPicked={handleUpdateModel}
      disabled={disabled}
      type={'global'}
      variant={'invisible'}
      isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
      autoPickedModel={autoPickedModel}
      showCategories
      showPreviewModelsSeparately={false}
      showTriggerIcon={showTriggerIcon}
      size={size}
      responsiveIconOnly={responsiveIconOnly}
      tooltipDirection={tooltipDirection}
      autoTierPicker={autoTierPicker}
      organizationDefaultModelId={organizationDefaultModelId}
      onOpenChange={handleOpenChange}
    />
  )
}
